import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

/**
 * ErrorBoundary — fanger opp uventede React-feil og viser en pen feilside
 * i stedet for blank hvit skjerm.
 *
 * Props:
 *  - resetKey: når denne endrer seg (f.eks. ruteendring) nullstilles feilen
 *    automatisk slik at en forbigående feil på én side ikke låser hele appen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Backstop: chunk-feil etter ny deploy → last siden på nytt én gang.
    const isChunkError =
      error && (error.name === 'ChunkLoadError' ||
                /Loading chunk [\w-]+ failed/i.test(error.message || ''));
    if (isChunkError) {
      let attempted = false;
      try { attempted = !!window.sessionStorage.getItem('chunk_reload_attempted'); } catch (e) {}
      if (!attempted) {
        try { window.sessionStorage.setItem('chunk_reload_attempted', '1'); } catch (e) {}
        window.location.reload();
        return;
      }
    }
    // Alltid logg til konsoll (også i produksjon) for diagnostikk.
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps) {
    // Nullstill feilen når ruten endrer seg (resetKey) — da slipper brukeren
    // å laste hele siden på nytt for å komme videre.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const details = [
        this.state.error?.toString(),
        this.state.errorInfo?.componentStack,
      ]
        .filter(Boolean)
        .join('\n');

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6">
              <AlertTriangle size={32} className="text-amber-600" />
            </div>

            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Noe gikk galt
            </h1>
            <p className="text-gray-500 mb-6">
              En uventet feil oppstod. Prøv å laste siden på nytt, eller gå tilbake til oversikten.
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium"
                data-testid="error-reload-btn"
              >
                <RotateCcw size={16} />
                Last inn på nytt
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                data-testid="error-go-home-btn"
              >
                <Home size={16} />
                Til oversikten
              </button>
            </div>

            {details && (
              <details className="mt-6 text-left" data-testid="error-details">
                <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-600 select-none">
                  Tekniske detaljer (kopier ved feilmelding)
                </summary>
                <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto max-h-48 text-red-700 whitespace-pre-wrap">
                  {details}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
